import { 
  format, 
  addDays, 
  startOfDay, 
  differenceInMinutes, 
  differenceInCalendarDays,
} from "date-fns";

/**
 * "08:30" → số phút
 */
export const timeToMinutes = (timeStr) => {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

/**
 * Assignment + Shift → Gantt bar
 */
export const assignmentToGanttBar = (assignment, baseDate) => {
  const shift = assignment.shiftId;
  if (!shift || !assignment.date) return null;

  // Assignment-level times override shift defaults (allow per-assignment customization)
  const startTime = assignment.startTime || shift.startTime;
  const endTime = assignment.endTime || shift.endTime;
  if (!startTime || !endTime) return null;

  const base = startOfDay(new Date(baseDate));
  const date = startOfDay(new Date(assignment.date));

  const dayOffset = differenceInCalendarDays(date, baseDate);
  if (dayOffset < 0) return null;

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  const shiftStart = new Date(date);
  shiftStart.setMinutes(startMinutes);

  let shiftEnd = new Date(date);
  shiftEnd.setMinutes(endMinutes);
  if (shift.crossesDay || endMinutes <= startMinutes) {
    shiftEnd = addDays(shiftEnd, 1);
  }

  const startSlot = differenceInMinutes(shiftStart, base) / 60;
  const duration = differenceInMinutes(shiftEnd, shiftStart) / 60;

  return {
    id: assignment._id,
    assignmentId: assignment._id,
    rawAssignment: assignment,
    shiftId: shift._id,
    date: assignment.date,

    startSlot,
    duration,

    shiftCode: shift.shiftCode,
    startTime,
    endTime,

    color: shift.color || "#2196f3",
    title: shift.shiftName || shift.shiftCode,
    requiredEmployees: assignment.requiredEmployees ?? shift.requiredEmployees,
    assignedCount: assignment.assignees?.length || 0,

    isLocked: assignment.isLocked,
    status: assignment.status,
    memo: assignment.memo,
  };
};

/**
 * Check if two time ranges overlap
 * @param {number} start1 - Start of first range
 * @param {number} end1 - End of first range
 * @param {number} start2 - Start of second range
 * @param {number} end2 - End of second range
 * @returns {boolean} - True if ranges overlap
 */
export const doTimeRangesOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

/**
 * Group assignments by date
 * @param {Array} assignments - Array of assignments
 * @returns {Map} - Map of date -> assignments[]
 */
export const groupAssignmentsByDate = (assignments) => {
  const grouped = new Map();
  
  assignments.forEach(assignment => {
    const dateKey = format(new Date(assignment.date), 'yyyy-MM-dd');
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey).push(assignment);
  });
  
  return grouped;
};

/**
 * Resolve working hours for one assignment.
 * Priority: assignment.workingTime → shift.workingTime → derived from start/end times.
 */
export const resolveWorkingTime = (assignment, baseDate) => {
  if (assignment.workingTime > 0) return assignment.workingTime;
  const shift = assignment.shiftId;
  if (shift?.workingTime > 0) return shift.workingTime;
  const bar = assignmentToGanttBar(assignment, baseDate ?? new Date());
  return bar?.duration ?? 0;
};

/**
 * Calculate total hours for assignments
 * @param {Array} assignments - Array of assignments
 * @returns {number} - Total hours
 */
export const calcTotalHours = (assignments) => {
  return assignments.reduce((total, a) => total + resolveWorkingTime(a), 0);
};

/**
 * Export schedule to Excel using ExcelJS + file-saver.
 * Columns: # | Code | Name | [dates…] | [shiftCode counts…] | TOTAL TIME (h)
 */
export const exportScheduleToExcel = async ({ employees, filteredEmployeeIds, startDate, endDate }) => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const wb = new ExcelJS.Workbook();
    wb.creator = 'DigitAir';
    wb.created = new Date();

    const start = new Date(startDate);
    const end   = new Date(endDate);

    // ── dates in range ──────────────────────────────────────────────────────────
    const dates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) dates.push(new Date(d));
    const dateKeySet = new Set(dates.map(d => d.toISOString().slice(0, 10)));

    // ── visible employees ───────────────────────────────────────────────────────
    const visible = filteredEmployeeIds?.length
        ? employees.filter(e => filteredEmployeeIds.includes(e.id))
        : employees;

    // ── collect all shift codes that appear in the range ────────────────────────
    const shiftCodeSet = new Set();
    for (const emp of visible) {
        for (const a of (emp.assignments || [])) {
            if (!a.shiftId || !a.date) continue;
            if (!dateKeySet.has(new Date(a.date).toISOString().slice(0, 10))) continue;
            const code = a.shiftId.shiftCode || a.shiftId.shiftName;
            if (code) shiftCodeSet.add(code);
        }
    }
    const shiftCodes = [...shiftCodeSet].sort();

    // ── column layout ────────────────────────────────────────────────────────────
    const COL_NO   = 1;
    const COL_CODE = 2;
    const COL_NAME = 3;
    const dateColStart  = 4;
    const statsColStart = dateColStart + dates.length;
    const totalTimeCol  = statsColStart + shiftCodes.length;
    const totalCols     = totalTimeCol;

    const ws = wb.addWorksheet('Schedule', {
        views: [{ state: 'frozen', xSplit: 3, ySplit: 3 }],
    });

    const fmt = (d) =>
        `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;

    // ── Row 1: Title ─────────────────────────────────────────────────────────────
    ws.mergeCells(1, 1, 1, totalCols);
    Object.assign(ws.getCell(1, 1), {
        value:     'Timekeeping',
        font:      { bold: true, size: 14 },
        alignment: { horizontal: 'center', vertical: 'middle' },
    });
    ws.getRow(1).height = 28;

    // ── Row 2: Date range ────────────────────────────────────────────────────────
    ws.mergeCells(2, 1, 2, totalCols);
    Object.assign(ws.getCell(2, 1), {
        value:     `${fmt(start)} - ${fmt(end)}`,
        font:      { italic: true, size: 10, color: { argb: 'FF666666' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
    });
    ws.getRow(2).height = 18;

    // ── Row 3: Headers ───────────────────────────────────────────────────────────
    const headerRow = ws.getRow(3);
    headerRow.height = 32;
    const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const THIN_BORDER = {
        top: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        right: { style: 'thin', color: { argb: 'FFAAAAAA' } },
    };

    const styleHeaderCell = (col, value, bgArgb) => {
        const cell = headerRow.getCell(col);
        cell.value     = value;
        cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border    = THIN_BORDER;
    };

    styleHeaderCell(COL_NO,   '#',    'FF1565C0');
    styleHeaderCell(COL_CODE, 'Code', 'FF1565C0');
    styleHeaderCell(COL_NAME, 'Name', 'FF1565C0');

    dates.forEach((d, i) => {
        const isWE = d.getDay() === 0 || d.getDay() === 6;
        styleHeaderCell(
            dateColStart + i,
            `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}\n${DAY_NAMES[d.getDay()]}`,
            isWE ? 'FF6A1B9A' : 'FF1565C0',
        );
    });

    shiftCodes.forEach((code, i) => {
        styleHeaderCell(statsColStart + i, code, 'FFE65100'); // deep orange
    });

    styleHeaderCell(totalTimeCol, 'TOTAL\nTIME (h)', 'FF1B5E20'); // dark green

    // ── Column widths ─────────────────────────────────────────────────────────────
    ws.getColumn(COL_NO).width   = 5;
    ws.getColumn(COL_CODE).width = 10;
    ws.getColumn(COL_NAME).width = 24;
    dates.forEach((_, i)       => { ws.getColumn(dateColStart + i).width  = 7; });
    shiftCodes.forEach((_, i)  => { ws.getColumn(statsColStart + i).width = 6; });
    ws.getColumn(totalTimeCol).width = 10;

    // ── Data rows ─────────────────────────────────────────────────────────────────
    const HAIR_BORDER = {
        top: { style: 'hair', color: { argb: 'FFCCCCCC' } },
        left: { style: 'hair', color: { argb: 'FFCCCCCC' } },
        bottom: { style: 'hair', color: { argb: 'FFCCCCCC' } },
        right: { style: 'hair', color: { argb: 'FFCCCCCC' } },
    };

    visible.forEach((emp, rowIdx) => {
        const r     = ws.getRow(4 + rowIdx);
        r.height    = 18;
        const bgAlt = rowIdx % 2 === 0 ? 'FFF5F5F5' : 'FFFFFFFF';

        const setCell = (col, val, opts = {}) => {
            const cell     = r.getCell(col);
            cell.value     = val;
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.bg || bgAlt } };
            cell.border    = HAIR_BORDER;
            cell.alignment = { horizontal: opts.center ? 'center' : 'left', vertical: 'middle' };
            if (opts.font) cell.font = opts.font;
        };

        setCell(COL_NO,   rowIdx + 1,                                    { center: true });
        setCell(COL_CODE, emp.employeeCode,                               { center: true });
        setCell(COL_NAME, emp.employee?.employeeName || emp.label);

        // Assignments in range only
        const inRange = (emp.assignments || []).filter(a =>
            a.date && dateKeySet.has(new Date(a.date).toISOString().slice(0, 10))
        );

        // date → shiftCode (multiple shifts same day joined with "/")
        const dateShiftMap = {};
        for (const a of inRange) {
            if (!a.shiftId) continue;
            const key  = new Date(a.date).toISOString().slice(0, 10);
            const code = a.shiftId.shiftCode || a.shiftId.shiftName || '';
            dateShiftMap[key] = dateShiftMap[key] ? `${dateShiftMap[key]}/${code}` : code;
        }

        // Date columns
        dates.forEach((d, di) => {
            const key    = d.toISOString().slice(0, 10);
            const code   = dateShiftMap[key] || '';
            const isWE   = d.getDay() === 0 || d.getDay() === 6;
            const cell   = r.getCell(dateColStart + di);
            cell.value     = code;
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb:
                code  ? 'FFE3F2FD' :
                isWE  ? 'FFF3E5F5' : bgAlt,
            }};
            cell.font      = code ? { bold: true, color: { argb: 'FF0D47A1' }, size: 9 } : {};
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border    = HAIR_BORDER;
        });

        // Shift count per code + total hours
        const shiftCnt = {};
        let totalHours = 0;
        for (const a of inRange) {
            if (!a.shiftId) continue;
            const code = a.shiftId.shiftCode || a.shiftId.shiftName;
            if (code) shiftCnt[code] = (shiftCnt[code] || 0) + 1;
            totalHours += resolveWorkingTime(a, start);
        }

        shiftCodes.forEach((code, i) => {
            const cnt = shiftCnt[code] || 0;
            setCell(statsColStart + i, cnt || '', {
                center: true,
                bg:     cnt ? 'FFFFF8E1' : bgAlt,
                font:   cnt ? { bold: true, color: { argb: 'FFE65100' } } : undefined,
            });
        });

        const hrs = Math.round(totalHours * 10) / 10;
        setCell(totalTimeCol, hrs, {
            center: true,
            bg:     'FFF1F8E9',
            font:   { bold: true, color: { argb: 'FF1B5E20' } },
        });
    });

    // ── Summary rows: total per shift code per day ───────────────────────────────
    const summaryData  = {};  // code → dateKey → count
    const summaryHours = {};  // code → totalHours
    for (const code of shiftCodes) {
        summaryData[code]  = {};
        summaryHours[code] = 0;
    }
    for (const emp of visible) {
        for (const a of (emp.assignments || [])) {
            if (!a.shiftId || !a.date) continue;
            const key  = new Date(a.date).toISOString().slice(0, 10);
            if (!dateKeySet.has(key)) continue;
            const code = a.shiftId.shiftCode || a.shiftId.shiftName;
            if (!code || !(code in summaryData)) continue;
            summaryData[code][key]  = (summaryData[code][key] || 0) + 1;
            summaryHours[code]     += resolveWorkingTime(a, start);
        }
    }

    const summaryStart = 4 + visible.length + 1; // +1 blank separator row

    // Separator row
    {
        const sepRow = ws.getRow(summaryStart - 1);
        sepRow.height = 6;
        for (let c = 1; c <= totalCols; c++) {
            const cell = sepRow.getCell(c);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDDDDD' } };
        }
    }

    shiftCodes.forEach((code, ci) => {
        const r = ws.getRow(summaryStart + ci);
        r.height = 18;

        // #, Code columns - blank with same background
        for (let c = COL_NO; c <= COL_CODE; c++) {
            r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } };
            r.getCell(c).border = HAIR_BORDER;
        }

        // Label
        Object.assign(r.getCell(COL_NAME), {
            value:     `${code}`,
            font:      { bold: true, color: { argb: 'FFE65100' }, size: 10 },
            fill:      { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } },
            alignment: { horizontal: 'left', vertical: 'middle' },
            border:    HAIR_BORDER,
        });

        // Date columns - count per day
        dates.forEach((d, di) => {
            const key = d.toISOString().slice(0, 10);
            const cnt = summaryData[code][key] || 0;
            const cell = r.getCell(dateColStart + di);
            cell.value     = cnt || '';
            cell.font      = cnt ? { bold: true, color: { argb: 'FFE65100' }, size: 9 } : {};
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: cnt ? 'FFFFF8E1' : 'FFFAFAFA' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border    = HAIR_BORDER;
        });

        // Stats columns - total appearances of this shift code
        const totalAppearances = Object.values(summaryData[code]).reduce((s, v) => s + v, 0);
        shiftCodes.forEach((sc, si) => {
            const cell = r.getCell(statsColStart + si);
            cell.value     = sc === code ? (totalAppearances || '') : '';
            cell.font      = sc === code && totalAppearances ? { bold: true, color: { argb: 'FFE65100' }, size: 9 } : {};
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: sc === code && totalAppearances ? 'FFFFF8E1' : 'FFFAFAFA' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border    = HAIR_BORDER;
        });

        // Total hours column
        const hrs = Math.round(summaryHours[code] * 10) / 10;
        Object.assign(r.getCell(totalTimeCol), {
            value:     hrs || '',
            font:      hrs ? { bold: true, color: { argb: 'FF1B5E20' }, size: 9 } : {},
            fill:      { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F8E9' } },
            alignment: { horizontal: 'center', vertical: 'middle' },
            border:    HAIR_BORDER,
        });
    });

    // ── Save ──────────────────────────────────────────────────────────────────────
    const p = (d, fn) => String(fn(d)).padStart(2, '0');
    const tag = (d) => `${p(d,x=>x.getDate())}${p(d,x=>x.getMonth()+1)}${d.getFullYear()}`;
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Timekeeping_${tag(start)}_${tag(end)}.xlsx`);
};

/**
 * Export a single employee's schedule vertically (date rows) - mirrors EmployeeScheduleDialog layout.
 * File name: Schedule_{employeeCode}_{startDate}_{endDate}.xlsx
 */
export const exportEmployeeScheduleToExcel = async ({ employee, startDate, endDate }) => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const wb = new ExcelJS.Workbook();
    wb.creator = 'DigitAir';
    wb.created = new Date();

    const start = new Date(startDate);
    const end   = new Date(endDate);

    const emp = employee?.employee || {};
    const employeeCode = emp.employeeCode || employee?.employeeCode || 'EMP';
    const employeeName = emp.employeeName || employee?.label || employeeCode;

    // ── Build date → assignments map ─────────────────────────────────────────────
    const assignmentsByDate = {};
    for (const a of (employee?.assignments || [])) {
        const key = a.date ? a.date.slice(0, 10) : null;
        if (!key) continue;
        if (!assignmentsByDate[key]) assignmentsByDate[key] = [];
        assignmentsByDate[key].push(a);
    }

    // ── Dates in range ───────────────────────────────────────────────────────────
    const dates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) dates.push(new Date(d));

    // ── Column layout ────────────────────────────────────────────────────────────
    const COL_DAY   = 1;  // Mon / Tue …
    const COL_DATE  = 2;  // dd/MM/yyyy
    const COL_CODE  = 3;  // shift code
    const COL_NAME  = 4;  // shift name
    const COL_START = 5;
    const COL_END   = 6;
    const COL_HOURS = 7;
    const COL_STAT  = 8;
    const TOTAL_COLS = 8;

    const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const fmt2 = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) => `${fmt2(d.getDate())}/${fmt2(d.getMonth()+1)}/${d.getFullYear()}`;

    const THIN_BORDER = {
        top:    { style: 'thin', color: { argb: 'FFAAAAAA' } },
        left:   { style: 'thin', color: { argb: 'FFAAAAAA' } },
        bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        right:  { style: 'thin', color: { argb: 'FFAAAAAA' } },
    };
    const HAIR_BORDER = {
        top:    { style: 'hair', color: { argb: 'FFCCCCCC' } },
        left:   { style: 'hair', color: { argb: 'FFCCCCCC' } },
        bottom: { style: 'hair', color: { argb: 'FFCCCCCC' } },
        right:  { style: 'hair', color: { argb: 'FFCCCCCC' } },
    };

    const STATUS_TEXT_COLOR = {
        PUBLISHED: 'FF1B5E20',
        DRAFT:     'FF616161',
        CANCELLED: 'FFB71C1C',
    };
    const STATUS_BG = {
        PUBLISHED: 'FFE8F5E9',
        DRAFT:     'FFF5F5F5',
        CANCELLED: 'FFFCE4EC',
    };

    const ws = wb.addWorksheet('Schedule', {
        views: [{ state: 'frozen', xSplit: 0, ySplit: 3 }],
    });

    // ── Row 1: Employee info ─────────────────────────────────────────────────────
    ws.mergeCells(1, 1, 1, TOTAL_COLS);
    Object.assign(ws.getCell(1, 1), {
        value:     `${employeeName}  (${employeeCode})`,
        font:      { bold: true, size: 13 },
        alignment: { horizontal: 'center', vertical: 'middle' },
    });
    ws.getRow(1).height = 26;

    // ── Row 2: Date range ────────────────────────────────────────────────────────
    ws.mergeCells(2, 1, 2, TOTAL_COLS);
    Object.assign(ws.getCell(2, 1), {
        value:     `${fmtDate(start)} - ${fmtDate(end)}`,
        font:      { italic: true, size: 10, color: { argb: 'FF666666' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
    });
    ws.getRow(2).height = 16;

    // ── Row 3: Headers ───────────────────────────────────────────────────────────
    const headerRow = ws.getRow(3);
    headerRow.height = 28;
    const headers = [
        { col: COL_DAY,   label: 'Day' },
        { col: COL_DATE,  label: 'Date' },
        { col: COL_CODE,  label: 'Code' },
        { col: COL_NAME,  label: 'Shift' },
        { col: COL_START, label: 'Start' },
        { col: COL_END,   label: 'End' },
        { col: COL_HOURS, label: 'Hours' },
        { col: COL_STAT,  label: 'Status' },
    ];
    for (const { col, label } of headers) {
        const cell = headerRow.getCell(col);
        cell.value     = label;
        cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border    = THIN_BORDER;
    }

    // ── Column widths ────────────────────────────────────────────────────────────
    ws.getColumn(COL_DAY).width   = 6;
    ws.getColumn(COL_DATE).width  = 12;
    ws.getColumn(COL_CODE).width  = 10;
    ws.getColumn(COL_NAME).width  = 22;
    ws.getColumn(COL_START).width = 8;
    ws.getColumn(COL_END).width   = 8;
    ws.getColumn(COL_HOURS).width = 8;
    ws.getColumn(COL_STAT).width  = 12;

    // ── Data rows ─────────────────────────────────────────────────────────────────
    let rowIdx = 0;
    let totalHours = 0;

    for (const d of dates) {
        const key    = d.toISOString().slice(0, 10);
        const shifts = assignmentsByDate[key] || [];
        const dayNum = d.getDay();
        const isWE   = dayNum === 0 || dayNum === 6;
        const bgDay  = isWE ? 'FFF3E5F5' : (rowIdx % 2 === 0 ? 'FFF5F5F5' : 'FFFFFFFF');

        if (shifts.length === 0) {
            // Empty day - one row, no shift info
            const r = ws.getRow(4 + rowIdx);
            r.height = 18;
            const setEmpty = (col, val, opts = {}) => {
                const cell = r.getCell(col);
                cell.value     = val;
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.bg || bgDay } };
                cell.alignment = { horizontal: opts.center ? 'center' : 'left', vertical: 'middle' };
                cell.border    = HAIR_BORDER;
                if (opts.font) cell.font = opts.font;
            };
            setEmpty(COL_DAY,   DAY_NAMES[dayNum], { center: true, font: isWE ? { color: { argb: 'FF7B1FA2' }, size: 9 } : { size: 9 } });
            setEmpty(COL_DATE,  fmtDate(d), { center: true });
            for (let c = COL_CODE; c <= TOTAL_COLS; c++) setEmpty(c, '');
            rowIdx++;
        } else {
            for (const a of shifts) {
                const shift   = a.shiftId || {};
                const ae      = a.assignees?.find(x => String(x.employeeId?._id ?? x.employeeId) === String(emp._id));
                const status  = ae?.status || 'DRAFT';
                const wt      = resolveWorkingTime(a, start);
                totalHours   += wt;

                // ARGB: AA=22 (~13% opacity) + RRGGBB from shift color → light tint on white
                const rawHex  = shift.color?.replace('#', '') || null;
                const bgShift = rawHex ? `22${rawHex}` : bgDay;

                const r = ws.getRow(4 + rowIdx);
                r.height = 18;

                const set = (col, val, opts = {}) => {
                    const cell = r.getCell(col);
                    cell.value     = val;
                    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.bg || bgDay } };
                    cell.alignment = { horizontal: opts.center ? 'center' : 'left', vertical: 'middle' };
                    cell.border    = HAIR_BORDER;
                    if (opts.font) cell.font = opts.font;
                };

                set(COL_DAY,   DAY_NAMES[dayNum], { center: true, font: isWE ? { color: { argb: 'FF7B1FA2' }, size: 9 } : { size: 9 } });
                set(COL_DATE,  fmtDate(d), { center: true });
                set(COL_CODE,  shift.shiftCode || '', { center: true, bg: bgShift, font: { bold: true, size: 9 } });
                set(COL_NAME,  shift.shiftName || shift.shiftCode || '', { bg: bgShift });
                set(COL_START, shift.startTime || '', { center: true });
                set(COL_END,   shift.endTime   || '', { center: true });
                set(COL_HOURS, wt || '', { center: true, bg: 'FFF1F8E9', font: wt ? { bold: true, color: { argb: 'FF1B5E20' } } : {} });
                set(COL_STAT,  status, { center: true, bg: STATUS_BG[status] || 'FFF5F5F5', font: { bold: true, color: { argb: STATUS_TEXT_COLOR[status] || 'FF616161' }, size: 9 } });

                rowIdx++;
            }
        }
    }

    // ── Total row ─────────────────────────────────────────────────────────────────
    const totalRow = ws.getRow(4 + rowIdx);
    totalRow.height = 20;
    ws.mergeCells(4 + rowIdx, COL_DAY, 4 + rowIdx, COL_END);
    Object.assign(totalRow.getCell(COL_DAY), {
        value:     'TOTAL',
        font:      { bold: true, size: 10, color: { argb: 'FF1B5E20' } },
        fill:      { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border:    THIN_BORDER,
    });
    Object.assign(totalRow.getCell(COL_HOURS), {
        value:     Math.round(totalHours * 10) / 10,
        font:      { bold: true, color: { argb: 'FF1B5E20' }, size: 11 },
        fill:      { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border:    THIN_BORDER,
    });
    for (let c = COL_STAT; c <= TOTAL_COLS; c++) {
        totalRow.getCell(c).fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
        totalRow.getCell(c).border = THIN_BORDER;
    }

    // ── Save ──────────────────────────────────────────────────────────────────────
    const p2 = (d, fn) => String(fn(d)).padStart(2, '0');
    const tag = (d) => `${p2(d,x=>x.getDate())}${p2(d,x=>x.getMonth()+1)}${d.getFullYear()}`;
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Schedule_${employeeCode}_${tag(start)}_${tag(end)}.xlsx`);
};

export const cascadeBars = (bars) => {
  if (!Array.isArray(bars) || bars.length === 0) return { bars: [], laneCount: 0 };

  const lanes = [];

  const sorted = [...bars]?.sort(
    (a, b) => a.startSlot - b.startSlot
  );

  sorted?.forEach(bar => {
    let placed = false;

    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];

      if (bar.startSlot >= last.startSlot + last.duration) {
        lanes[i].push(bar);
        bar.laneIndex = i;
        placed = true;
        break;
      }
    }

    if (!placed) {
      bar.laneIndex = lanes.length;
      lanes.push([bar]);
    }
  });

  return { bars: sorted, laneCount: lanes.length };
};
